import React from "react";
import { observer } from "mobx-react-lite";
import { Expression,ExpressionArray, ExpressionWrapper,isUnaryLogicalExpression,isBinaryArithmeticExpression, isLogicalExpression, isDataFieldPathExpression, isLiteralExpression, isFunctionExpression, isComparisonExpression, isSetComparisonExpression, isRangeComparisonExpression, isUnaryComparisonExpression, isUnaryArithmeticExpression, isStringComparisonExpression, isLeftOperandOnly, isCaseExpression } from "../types2";
import BuilderInteractor from "../builder.interactor";
import { ExpMenu } from "./ExpMenu";
import { LogicalExpression } from "./LogicalExpression";
import { DataFieldPathExpression } from "./DataFieldPathExpression";
import { LiteralExpression } from "./LiteralExpression";
import { FunctionExpression } from "./FunctionExpression";
import { OperatorSelect } from "./OperatorSelect";
import { UnaryOperatorSelect } from "./UnaryOperatorSelect";
import { DeleteOutlined, PlusCircleFilled, PlusCircleOutlined } from "@ant-design/icons";
import { CaseExpression } from "./CaseExpression";
import { ArithmeticOperatorSelect } from "./ArithmeticOperatorSelect";

// Helper function to unwrap ExpressionWrapper
const unwrapExpression = (expr: Expression | ExpressionWrapper): Expression => {
  return "expression" in expr ? expr.expression : expr;
};

// Add type guard to check if expression is an array
const isExpressionArray = (expr: any): expr is Expression[] => {
  return Array.isArray(expr);
};

// Helper function to check if path is for right operand
const isRightSideOperand = (path: string[]): boolean => {
  const lastSegment = path[path.length - 1];
  const parentSegment = path[path.length - 2];
  return lastSegment === "rightOperand" || 
         parentSegment === "rightOperands" || 
         /rightOperands\.\d+/.test(lastSegment);
};

// Helper function to get dataType from an expression
const getDataType = (expr: Expression | ExpressionWrapper): string => {
  if (!expr) return "Integer Number";

  const unwrapped = unwrapExpression(expr);

  if (isLiteralExpression(unwrapped) || isDataFieldPathExpression(unwrapped)) {
    return unwrapped.dataType || "Integer Number";
  }

  if (isBinaryArithmeticExpression(unwrapped) && unwrapped.operands?.[0]) {
    return getDataType(unwrapped.operands[0]);
  }

  if (isComparisonExpression(unwrapped)) {
    return getDataType(unwrapped.leftOperand);
  }

  if (isSetComparisonExpression(unwrapped) || isRangeComparisonExpression(unwrapped)) {
    return getDataType(unwrapped.leftOperand);
  }

  return "Integer Number";
};

export const ExpressionRenderer: React.FC<{
  expr: Expression | ExpressionWrapper | undefined;
  path: string[];
  interactor: BuilderInteractor;
  requiredExpMenu?: boolean;
}> = observer(({ expr, path, interactor, requiredExpMenu }: {
  expr: Expression | ExpressionWrapper | undefined;
  path: string[];
  interactor: BuilderInteractor;
  requiredExpMenu?: boolean;
}) => {
  const unwrappedExpr = expr ? unwrapExpression(expr) : undefined;

  console.log("ExpressionRenderer", expr, unwrappedExpr, path);
  if (!unwrappedExpr) {
    return <span className="sql-error">Undefined Expression</span>;
  }
  

  if (isLogicalExpression(unwrappedExpr)) {
    return (
      <LogicalExpression
        expr={unwrappedExpr}
        path={path}
        interactor={interactor}
      />
    );
  }

  if (isDataFieldPathExpression(unwrappedExpr)) {
    const isRightOperand = isRightSideOperand(path);
    
    return (
      <div className="expression-container">
        {requiredExpMenu && (
          <span className="exp-menu-icon">
            <ExpMenu
              onChange={(newValue) => {
                interactor.updateExpressionInputType([...path], newValue);
              }}
            />
          </span>
        )}
        <span>
          <DataFieldPathExpression
            value={unwrappedExpr.dataFieldPath}
            dataType={isRightOperand ? unwrappedExpr.dataType : undefined}
            onChange={(newValue) => {
              interactor.updateExpression([...path, "dataFieldPath"], newValue);
            }}
            onDataTypeChange={(newValue) => {
              interactor.updateDataType([...path, "dataType"], newValue);
              
              if (!isRightOperand) {
                const parentPath = path.slice(0, -1);
                interactor.updateRightOperandsDataType(parentPath, newValue);
              }
            }}
          />
        </span>
      </div>
    );
  }

  if (isLiteralExpression(unwrappedExpr)) {
    let placeHolder = "Enter the value here";
    if (unwrappedExpr.placeHolder != undefined)
      placeHolder = unwrappedExpr.placeHolder;
    
    const isRightOperand = isRightSideOperand(path);
    
    return (
      <div className="expression-container">
        {requiredExpMenu && !isRightOperand && (
          <span className="exp-menu-icon">
            <ExpMenu
              onChange={(newValue) => {
                interactor.updateExpressionInputType([...path], newValue);
              }}
            />
          </span>
        )}
        <span>
          <LiteralExpression
            value={unwrappedExpr.literalValue}
            dataType={unwrappedExpr.dataType}
            placeHolder={placeHolder}
            onChange={(newValue) => {
              interactor.updateExpression([...path, "literalValue"], newValue);
            }}
            onDataTypeChange={(newValue) => {
              interactor.updateDataType([...path, "dataType"], newValue);
            }}
            showDataTypeSelect={!isRightOperand}
          />
        </span>
      </div>
    );
  }

  if (isExpressionArray(unwrappedExpr)) {
    return (
      <div className="expression-container">
        {unwrappedExpr.map((item: Expression, index: number) => (
          <ExpressionRenderer
            key={index}
            expr={item}
            path={[...path, index.toString()]}
            interactor={interactor}
            requiredExpMenu={true}
          />
        ))}
         <div>
          <div onClick={() => interactor.addFxArgs([...path])}>
            Add Item
          </div>
         </div>
      </div>
     
    );
  }

  if (isFunctionExpression(unwrappedExpr)) {
    return (
      <div className="function-container">
        <div className="expression-container">
          {requiredExpMenu && (
            <span className="exp-menu-icon">
              {" "}
              <ExpMenu
                onChange={(newValue) => {
                  interactor.updateExpressionInputType([...path], newValue);
                }}
              />
            </span>
          )}
          <span>
            <FunctionExpression
              value={unwrappedExpr.fxName}
              onChange={(functionObj) => {
                interactor.updateExpression(
                  [...path, "fxName"],
                  functionObj.name
                );
                if(functionObj.inputType!=undefined){
                  interactor.updateExpression(
                    [...path, "inputType"],
                    functionObj.inputType
                  );  
                }
                interactor.updateFunctionsParams(
                  [...path, "fxArgs"],
                  functionObj.parameters
                );
              }}
            />
          </span>
        </div>
        <div className="function-args-container">
          {unwrappedExpr.fxArgs &&
            unwrappedExpr.fxArgs.map((args:any, index) => (
              <div key={index} className="function-arg-item">
                <div className="function-arg-line"></div>
                <div className="function-arg-content">
                  <ExpressionRenderer
                    expr={args}
                    path={[...path, "fxArgs", index.toString()]}
                    interactor={interactor}
                    requiredExpMenu={true}
                  />
                  {unwrappedExpr.inputType=="string[]" && unwrappedExpr.fxArgs.length>1 && (
                   <div onClick={() => interactor.removeFxArgs([...path], index)}>
                    <DeleteOutlined />
                   </div>
                  )}
                </div>
              </div>
            ))}
            {unwrappedExpr.inputType=="string[]" && (
              <div className="function-arg-add-button" onClick={() => interactor.addFxArgs([...path])}>
                <PlusCircleFilled />
              </div>
            )}
        </div>
      </div>
    );
  }

  if (isBinaryArithmeticExpression(unwrappedExpr)) {
    // Get dataType from first operand to propagate
    const firstOperand = unwrappedExpr.operands?.[0];
    let firstOperandDataType = "Integer Number";
    
    if (firstOperand && (isLiteralExpression(firstOperand) || isDataFieldPathExpression(firstOperand))) {
      firstOperandDataType = firstOperand.dataType || "Integer Number";
    }

    return (
      <div className="expression-container">
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '3px',
          border: '1px solid #d9d9d9',
          padding: '8px',
          borderRadius: '4px',
          backgroundColor: '#f5f5f5'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            marginRight: '8px'
          }}>
            <ArithmeticOperatorSelect
              value={unwrappedExpr.operator}
              onChange={(newOperator: string) =>
                interactor.updateArithmeticOperator([...path], newOperator)
              }
              className="operator-select"
            />
            <PlusCircleFilled 
              onClick={() => interactor.addArithmeticOperand([...path])}
              style={{ cursor: 'pointer', fontSize: '16px', color: '#1890ff' }}
              title="Add operand"
            />
            <DeleteOutlined 
              onClick={() => interactor.resetToComparisonExpression(path)}
              style={{ cursor: 'pointer', fontSize: '16px', color: '#ff4d4f' }}
              title="Reset to comparison"
            />
          </div>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: '8px' 
          }}>
            {unwrappedExpr.operands && unwrappedExpr.operands.map((operand: any, index: number) => (
              <div key={index} style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <ExpressionRenderer
                  expr={{
                    ...operand,
                    dataType: firstOperandDataType // Pass dataType to all operands
                  }}
                  path={[...path, "operands", index.toString()]}
                  interactor={interactor}
                  requiredExpMenu={true}
                />
                <PlusCircleFilled 
                  onClick={() => interactor.convertToArithmeticExpression(path, index)}
                  style={{ 
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#1890ff'
                  }}
                  title="Convert to arithmetic group"
                />
                <DeleteOutlined 
                  onClick={() => interactor.removeArithmeticOperand([...path], index)}
                  style={{ 
                    cursor: 'pointer',
                    fontSize: '16px',
                    color: '#ff4d4f'
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isComparisonExpression(unwrappedExpr) || 
      isSetComparisonExpression(unwrappedExpr) || 
      isRangeComparisonExpression(unwrappedExpr)) {
    let leftOperand: any = unwrappedExpr.leftOperand;
    
    return (
      <div className="expression-container">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          {unwrappedExpr.leftOperand && (
            <>
              <ExpressionRenderer
                expr={unwrappedExpr.leftOperand}
                path={[...path, "leftOperand"]}
                interactor={interactor}
                requiredExpMenu={true}
              />
              <PlusCircleFilled 
                onClick={() => interactor.convertToArithmeticExpression([...path, "leftOperand"])}
                style={{ 
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#1890ff'
                }}
                title="Add arithmetic operation"
              />
            </>
          )}{" "}
          <OperatorSelect
            value={unwrappedExpr.operator}
            onChange={(newOperator: string) =>
              interactor.updateOperator(path, newOperator)
            }
            dataType={getDataType(leftOperand)}
            className="operator-select"
          />
          {isComparisonExpression(unwrappedExpr) && unwrappedExpr.rightOperand && (
            <div className="expression-container">
              <span className="exp-menu-icon">
                <ExpMenu
                  onChange={(newValue) => {
                    interactor.updateExpressionInputType([...path, "rightOperand"], newValue);
                  }}
                />
              </span>
              <ExpressionRenderer
                expr={{
                  ...unwrappedExpr.rightOperand,
                  dataType: getDataType(leftOperand)
                }}
                path={[...path, "rightOperand"]}
                interactor={interactor}
                requiredExpMenu={false}
              />
            </div>
          )}
          {(isSetComparisonExpression(unwrappedExpr) || isRangeComparisonExpression(unwrappedExpr)) && 
            unwrappedExpr.rightOperands && (
            <div className="right-operands-container" style={{ marginLeft: '20px' }}>
              {unwrappedExpr.rightOperands.map((rightOp: ExpressionWrapper, index: number) => (
                <div 
                  key={index} 
                  className="right-operand-item"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}
                >
                  <span className="exp-menu-icon">
                    <ExpMenu
                      onChange={(newValue) => {
                        interactor.updateExpressionInputType(
                          [...path, "rightOperands", index.toString()], 
                          newValue
                        );
                      }}
                    />
                  </span>
                  <div style={{ flex: 1 }}>
                    {/* Get dataType from first operand if it's a binary arithmetic expression */}
                    {/* {isBinaryArithmeticExpression(leftOperand) ? 
                      getDataType(leftOperand) :
                      getDataType(leftOperand)} */}
                    <ExpressionRenderer
                      expr={{
                        ...rightOp,
                        dataType: getDataType(leftOperand)
                      }}
                      path={[...path, "rightOperands", index.toString()]}
                      interactor={interactor}
                      requiredExpMenu={false}
                    />
                  </div>
                  {unwrappedExpr.rightOperands.length > 1 && (
                    <DeleteOutlined 
                      onClick={() => interactor.removeRightOperand(path, index)}
                      style={{ 
                        marginLeft: '8px', 
                        cursor: 'pointer',
                        fontSize: '16px',
                        color: '#ff4d4f'
                      }}
                    />
                  )}
                </div>
              ))}
              <PlusCircleFilled 
                onClick={() => interactor.addRightOperand(path)}
                style={{ 
                  marginTop: '8px', 
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#1890ff'
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isUnaryLogicalExpression(unwrappedExpr)){
    return (
      <div className="expression-container">
        <ExpressionRenderer expr={unwrappedExpr.operand} path={[...path, "operand"]} interactor={interactor} requiredExpMenu={true} />
      </div>
    );
  }

  if (isUnaryComparisonExpression(unwrappedExpr)) {
    return (
      <div className="expression-container">
        {unwrappedExpr.operand && (
          <ExpressionRenderer
            requiredExpMenu={true}
            expr={unwrappedExpr.operand}
            path={[...path, "operand"]}
            interactor={interactor}
          />
        )}{" "}
        <OperatorSelect
          value={unwrappedExpr.operator}
          onChange={(newOperator: string) =>
            interactor.updateOperator(path, newOperator)
          }
          className="operator-select"
        />
      </div>
    );
  }

  if (isUnaryArithmeticExpression(unwrappedExpr)) {
    return (
      <div className="expression-container">
        {requiredExpMenu && (
          <span className="exp-menu-icon">
            <ExpMenu
              onChange={(newValue) => {
                interactor.updateExpressionInputType([...path], newValue);
              }}
            />
          </span>
        )}
        <UnaryOperatorSelect
          value={unwrappedExpr.operator}
          onChange={(newOperator: string) =>
            interactor.updateUnaryOperator(path, newOperator)
          }
          className="operator-select"
        />
        {unwrappedExpr.operand && (
          <ExpressionRenderer
            requiredExpMenu={true}
            expr={unwrappedExpr.operand}
            path={[...path, "operand"]}
            interactor={interactor}
          />
        )}
      </div>
    );
  }

  if (isStringComparisonExpression(unwrappedExpr)) {
    return (
      <span>
        {unwrappedExpr.leftOperand && (
          <ExpressionRenderer
            requiredExpMenu={true}
            expr={unwrappedExpr.leftOperand}
            path={[...path, "leftOperand"]}
            interactor={interactor}
          />
        )}{" "}
        <span className="sql-operator">{unwrappedExpr.operator}</span>{" "}
        {unwrappedExpr.rightOperands &&
          unwrappedExpr.rightOperands.map((operand, index) => (
            <React.Fragment key={index}>
              {index > 0 && " "}
              <ExpressionRenderer
                requiredExpMenu={true}
                expr={operand}
                path={[...path, "rightOperands", index.toString()]}
                interactor={interactor}
              />
            </React.Fragment>
          ))}
      </span>
    );
  }

  if (isLeftOperandOnly(unwrappedExpr)) {
    return (
      <div className="expression-container">
        <ExpressionRenderer
          requiredExpMenu={true}
          expr={unwrappedExpr.leftOperand}
          path={[...path, "leftOperand"]}
          interactor={interactor}
        />
        <OperatorSelect
          value={unwrappedExpr.operator}
          onChange={(newOperator: string) =>
            interactor.updateOperator(path, newOperator)
          }
          className="operator-select"
        />
        <ExpressionRenderer
          requiredExpMenu={true}
          expr={unwrappedExpr.rightOperand}
          path={[...path, "rightOperand"]}
          interactor={interactor}
        />
      </div>
    );
  }

  if (isCaseExpression(unwrappedExpr)) {
    return (
      <div className="case-when-expression-container">
       <span className="case-exp-menu"><ExpMenu
              onChange={(newValue) => {
                interactor.updateExpressionInputType([...path], newValue);
              }}
            /></span>
      <CaseExpression 
        expr={unwrappedExpr} 
        path={path} 
        interactor={interactor} 
      />
      </div>
    );
  }

  // Handle other types of expressions (e.g., LiteralExpression, etc.)
  return <span className="expression">{JSON.stringify(unwrappedExpr)}</span>;
}); 